/**
 * THE DELIVERY TICK (ITP-E-194-08, carbon's `.itp-msgr-tick`): on this user's own messages, after the time, in the thread
 * and before the last line of a list row. Sending is local (the message is not back from the server yet); sent, read by
 * some and read by all are today's `readStatus`; delivered is the server's `Delivered` (ITP-E-207-01), or B-17's
 * `deliveredUpTo` while its flag is on.
 */
export type MessageTick = 'sending' | 'sent' | 'delivered' | 'someRead' | 'read';

/** The glyph and the words for each tick. Read is a second ink and different words, never the ink alone. */
export const MESSAGE_TICKS: Readonly<Record<MessageTick, {icon: string, label: string}>> = {
  sending: {icon: 'fa-regular fa-clock', label: 'Sending'},
  sent: {icon: 'fa-solid fa-check', label: 'Sent'},
  delivered: {icon: 'fa-solid fa-check-double', label: 'Delivered'},
  someRead: {icon: 'fa-solid fa-check', label: 'Read by some'},
  read: {icon: 'fa-solid fa-check-double', label: 'Read'}
};

/**
 * The tick of one message, or null when it takes none (another member's message, a removed or a system one).
 * `deliveredUpTo` is the sequence B-17 says reached every member; null while that is not known.
 */
export function tickOf(message: any, deliveredUpTo: number | null = null): MessageTick | null {
  if (!message?.isMine || message.isDeleted || message.isSystemMessage) {
    return null;
  }
  if (message.isTemporary) {
    return 'sending';
  }
  if (message.readStatus === 'AllRead') {
    return 'read';
  }
  // B-17: the server already knows it reached every other member; no need to wait for `deliveredUpTo`.
  if (message.readStatus === 'Delivered') {
    return 'delivered';
  }
  if (deliveredUpTo !== null && message.sequence != null && message.sequence <= deliveredUpTo) {
    return 'delivered';
  }
  return message.readStatus === 'SomeRead' ? 'someRead' : 'sent';
}
